import React, { useEffect, useState } from 'react';
import { Calendar, Download, ExternalLink } from 'lucide-react';

/**
 * Phase 26.7 — "Add to Calendar" button trio.
 *
 * Props:
 *   variant   'internal' | 'public'  (drives which API path we call)
 *   eventId    string
 *   compact    boolean               (renders as three inline buttons; default true)
 *   className  string                 (optional wrapper classes)
 *
 * The component fetches the calendar-links bundle from the backend once,
 * then renders three actions:
 *   • Google Calendar  (opens https://calendar.google.com template URL)
 *   • Outlook          (opens Outlook Web compose URL; Outlook desktop
 *                       users should use "Download .ics" instead)
 *   • Download .ics    (browser downloads the file; Apple Calendar opens it)
 *
 * All URLs come from the server — never re-derive them client-side so
 * escaping, timezone, and visibility rules stay in one place.
 */
export default function AddToCalendar({
  variant = 'internal',
  eventId,
  compact = true,
  className = '',
}) {
  const [links, setLinks] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!eventId) return undefined;
    let cancelled = false;
    const base = process.env.REACT_APP_BACKEND_URL;
    const path = variant === 'public'
      ? `/api/public/calendar/events/${eventId}/calendar-links`
      : `/api/calendar/events/${eventId}/calendar-links`;
    const url = `${base}${path}`;
    const token = variant === 'internal' ? localStorage.getItem('token') : null;
    fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((data) => { if (!cancelled) setLinks(data); })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [eventId, variant]);

  if (error || !links) {
    // Fail silently — no calendar block if unavailable; the rest of the UI
    // keeps working.
    return null;
  }

  // Phase 27.30.10 — For the internal variant, always rewrite the ICS
  // URL to the CURRENT frontend backend origin. The backend builds it
  // from PORTAL_URL which correctly points to production, but the
  // preview environment (or any other origin) shares the code and would
  // otherwise fetch cross-origin against a host that doesn't have the
  // preview event. The path segment is stable, so we just swap the
  // origin. Public ICS URLs are left as-is (anonymous access, any origin
  // that serves the app also serves the public API).
  let icsHref = links.ics_url;
  if (variant === 'internal' && icsHref && eventId) {
    const origin = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
    icsHref = `${origin}/api/calendar/events/${eventId}/ics`;
  }
  const googleHref = links.google_url;
  const outlookHref = links.outlook_url;

  const base = 'inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition';
  const tone = 'border-slate-300 text-slate-700 hover:bg-slate-50 hover:border-slate-400';

  return (
    <div
      className={`flex flex-wrap items-center gap-2 ${className}`}
      data-testid="add-to-calendar"
    >
      {!compact && (
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 mr-1">
          <Calendar size={12} className="inline mr-1" /> Add to calendar
        </span>
      )}
      {googleHref && (
        <a
          href={googleHref}
          target="_blank"
          rel="noreferrer"
          className={`${base} ${tone}`}
          data-testid="add-to-google-calendar"
        >
          <ExternalLink size={12} /> Google Calendar
        </a>
      )}
      {outlookHref && (
        <a
          href={outlookHref}
          target="_blank"
          rel="noreferrer"
          className={`${base} ${tone}`}
          data-testid="add-to-outlook"
        >
          <ExternalLink size={12} /> Outlook
        </a>
      )}
      {icsHref && (
        <button
          type="button"
          onClick={async () => {
            // Phase 27.30.10 — auth-safe .ics download. Public variant
            // hits an anonymous endpoint and works with a bare <a href>,
            // but internal variant needs the Bearer token, else the
            // browser downloads a "Not authenticated" JSON blob.
            try {
              const token = variant === 'internal' ? localStorage.getItem('token') : null;
              const r = await fetch(icsHref, {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
              });
              if (!r.ok) throw new Error(`ICS download failed (HTTP ${r.status})`);
              const blob = await r.blob();
              const objUrl = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = objUrl;
              a.download = `event-${eventId}.ics`;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              setTimeout(() => URL.revokeObjectURL(objUrl), 1500);
            } catch (err) {
              // Fallback: open in a new tab so at least the public
              // variant keeps working when a browser blocks fetch.
              window.open(icsHref, '_blank', 'noopener');
            }
          }}
          className={`${base} ${tone}`}
          data-testid="download-ics"
        >
          <Download size={12} /> Download .ics
        </button>
      )}
    </div>
  );
}
